import { memoizeFunction, mergeStyleSets, useTheme } from '@fluentui/react';
import { useScreen } from 'common/hooks/useScreen';
import { IAppTheme } from 'common/theme';
import { isSmallScreen, isTinyScreen, ScreenSize } from 'common/utilities/ResponsiveBreakpoints';

const getStyles = memoizeFunction((theme: IAppTheme, screenSize: ScreenSize, mini: boolean) => {
  const smallScreen = isSmallScreen(screenSize);
  const tinyScreen = isTinyScreen(screenSize);
  let cardHeight: string | number = 480;
  if (smallScreen) {
    cardHeight = 'auto';
  } else if (mini) {
    cardHeight = 232;
  }

  const noBackdropStyle = {
    backgroundBlendMode: 'none',
    backdropFilter: 'none',
  };

  return mergeStyleSets({
    cardContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: tinyScreen ? 'auto' : 360,
      height: cardHeight,
      minHeight: smallScreen ? 100 : 200,
      overflowY: 'auto',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      background: theme.appColors?.cardContentBackground,
      backgroundBlendMode: 'normal, luminosity',
      // No need to use getBackdropFilterFallback here:
      backdropFilter: 'blur(60px)',
      borderRadius: '8px',
      boxShadow: theme.effects.elevation8,
      '&:hover': {
        boxShadow: theme.effects.elevation16,
      },
    },
    shimmerContainer: {
      padding: smallScreen ? 24 : 32,
    },
    errorCardContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: theme.spacing.s1,
      padding: smallScreen ? '24px 16px' : '32px',
    },
    alertCardContainer: {
      background: 'none',
      ...noBackdropStyle,
      '&, &:hover': {
        boxShadow: 'none !important',
      },
    },
    featureCardContent: {
      background: theme.palette.white,
      borderRadius: '8px',
      boxShadow: theme.effects.elevation16,
      padding: smallScreen ? '24px 0 16px 16px' : '32px 0px 16px 32px',
    },
    featureCardWithFooter: {
      borderRadius: '8px 8px 0px 0px',
    },
    alertCardContent: {
      padding: smallScreen ? '0px 16px' : '0px',
    },
    cardFooter: {
      padding: smallScreen ? '24px 16px' : '24px 32px',
      borderRadius: '0px 0px 8px 8px',
    },
  });
});

export const useDashboardCardStyles = (mini: boolean) => {
  const theme = useTheme() as IAppTheme;
  const { screenSize } = useScreen();
  return getStyles(theme, screenSize, mini);
};
